import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';
import NavigateBeforeRoundedIcon from '@mui/icons-material/NavigateBeforeRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import axios from 'axios';
import LeaderLineDonut from './LeaderLineDonut';
import Apiurl from '../../../../Helpers/apiUrl';

const ITEMS_PER_PAGE = 10;

const MonthlyTrendPreview = ({ globalFilters }) => {
  const navigate = useNavigate();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Extract raw payload context safely for navigation usage
  const parsedContext = useMemo(() => {
    const rawContext = sessionStorage.getItem('dash_drawer_context');
    if (rawContext) {
      try { return JSON.parse(rawContext); } catch (e) {}
    }
    return null;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('u_token');
        const fmt = (arr) => (arr?.length > 0 ? arr.join(',') : '');
        const contextMonth = parsedContext?.selectedMonth || '';

        const queryParams = {
          regions: fmt(globalFilters?.regions),
          states: fmt(globalFilters?.states),
          roles: fmt(globalFilters?.roles),
          collections: fmt(globalFilters?.collections),
          categories: fmt(globalFilters?.categories),
          priceSegments: fmt(globalFilters?.prices),
          emp_codes: fmt(globalFilters?.users),
          brands: fmt(globalFilters?.brands),
          years: fmt(globalFilters?.years),
          months: contextMonth ? contextMonth : fmt(globalFilters?.months),
        };

        const response = await axios.get(
          `${Apiurl}admin/dash-distribution`,
          {
            params: queryParams,
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        setApiData(response.data?.data || response.data || {
          usersQuoteDistribution: [],
          regionQuoteDistribution: [],
          stateQuoteDistribution: []
        });

        setSearchTerm('');
        setCurrentPage(1);
      } catch (err) {
        setError(err.message || "Failed to fetch analytics profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [globalFilters, parsedContext]);

  const handleUserClick = (userId) => {
    
    navigate('/admin/projects', {
      state: {
        years: globalFilters?.years || [],
        users: [userId],
        roles: globalFilters?.roles || [],
        regions: globalFilters?.regions || [],
        states: globalFilters?.states || [],
      months: parsedContext?.selectedMonth ? [Number(parsedContext.selectedMonth)] : globalFilters?.months || []
      }
    });
  };

  const handleRegionClick = (regionName) => {
  if (!regionName) return;
  navigate('/admin/projects', {
    state: {
      years: globalFilters?.years || [],
      users: globalFilters?.users || [],
      roles: globalFilters?.roles || [],
      regions: [regionName], 
      states: globalFilters?.states || [],
      months: parsedContext?.selectedMonth ? [Number(parsedContext.selectedMonth)] : globalFilters?.months || []
    }
  });
};

const handleStateClick = (stateName) => {
  if (!stateName) return;
  navigate('/admin/projects', {
    state: {
      years: globalFilters?.years || [],
      users: globalFilters?.users || [],
      roles: globalFilters?.roles || [],
      regions: globalFilters?.regions || [],
      states: [stateName], 
      months: parsedContext?.selectedMonth ? [Number(parsedContext.selectedMonth)] : globalFilters?.months || []
    }
  });
};

  const filteredUsers = useMemo(() => {
    const users = apiData?.usersQuoteDistribution || [];
    if (!searchTerm.trim()) return users;

    const target = searchTerm.toLowerCase().trim();
    return users.filter(user => {
      const fullName = `${user.name || ''} ${user.last_name || ''}`.toLowerCase();
      return (
        String(user.empCode || '').toLowerCase().includes(target) ||
        fullName.includes(target) ||
        String(user.region || '').toLowerCase().includes(target) ||
        String(user.state || '').toLowerCase().includes(target)
      );
    });
  }, [apiData, searchTerm]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE) || 1;

  const paginatedUsers = useMemo(() => {
    const startOffset = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(startOffset, startOffset + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center min-h-[450px] p-6">
        <CircularProgress size={28} sx={{ color: '#1E3A5F' }} />
        <span className="text-xs text-slate-400 font-semibold mt-4 tracking-wide">Loading performance analytics…</span>
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-red-600 text-xs font-semibold bg-red-50 rounded-xl border border-red-100 m-6">{error}</div>;
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">

      {/* PERFORMANCE TABLE */}
      <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

        <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-[13px] font-bold text-slate-800 tracking-wide">Performance Metrics Overview</h3>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">
              Select a user to view their detailed contribution breakdown.
            </p>
          </div>

          <div className="relative flex items-center">
            <SearchRoundedIcon className="absolute left-3 text-slate-400" sx={{ fontSize: 16 }} />
            <input
              type="text"
              placeholder="Search by name, emp code, region…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/15 focus:border-[#1E3A5F]/40 transition-all font-medium text-slate-700 placeholder-slate-400 bg-slate-50/70"
            />
          </div>
        </div>

        <div className="overflow-x-auto w-full">
          <table className="text-left border-collapse text-xs min-w-[900px] w-full table-fixed">
            <thead>
              <tr className="text-slate-500 bg-slate-50 border-b border-slate-200 uppercase font-bold tracking-wider text-[10px]">
                <th className="w-[15%] px-6 py-3">Emp Code</th>
                <th className="w-[30%] px-6 py-3">User Name</th>
                <th className="w-[20%] px-6 py-3">Region</th>
                <th className="w-[20%] px-6 py-3">State</th>
                <th className="w-[15%] px-6 py-3 text-right pr-8">Quotations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-600 font-medium">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-slate-400 font-medium text-xs">
                    No records match your search.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, idx) => (
                  <tr
                    key={ user.empCode || idx}
                    onClick={() => handleUserClick( String(user.empCode))}
                    className="hover:bg-slate-50 transition-colors cursor-pointer group"
                  >
                    <td className="px-6 py-3.5 font-mono text-[11px] text-slate-400 group-hover:text-[#1E3A5F] transition-colors">{user.empCode || 'N/A'}</td>
                    <td className="px-6 py-3.5 font-bold text-slate-800 group-hover:text-[#1E3A5F] transition-colors truncate">
                      {user.name || user.last_name ? `${user.name || ''} ${user.last_name || ''}`.trim() : 'Anonymous'}
                    </td>
                    <td className="px-6 py-3.5 text-slate-500 truncate">{user.region || '—'}</td>
                    <td className="px-6 py-3.5 text-slate-500 truncate">{user.state || '—'}</td>
                    <td className="px-6 py-3.5 text-right pr-8">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-bold text-slate-900 font-mono text-xs">{user.quotationCount ?? 0}</span>
                        <ChevronRightRoundedIcon className="text-slate-300 group-hover:text-[#1E3A5F] group-hover:translate-x-0.5 transition-all" sx={{ fontSize: 14 }} />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[11px] font-semibold text-slate-500">
            Showing <span className="text-slate-800 font-bold font-mono">{filteredUsers.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1}</span> to{' '}
            <span className="text-slate-800 font-bold font-mono">{Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)}</span> of{' '}
            <span className="text-[#1E3A5F] font-bold font-mono">{filteredUsers.length}</span> records
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-slate-600 transition flex items-center justify-center"
            >
              <NavigateBeforeRoundedIcon sx={{ fontSize: 18 }} />
            </button>

            <div className="flex items-center gap-1 px-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-7 h-7 rounded-lg text-xs font-bold font-mono transition-all ${
                    currentPage === pageNum
                      ? 'bg-[#1E3A5F] text-white'
                      : 'text-slate-500 bg-white border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="p-1.5 border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-slate-600 transition flex items-center justify-center"
            >
              <NavigateNextRoundedIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </div>

      {/* DISTRIBUTION CHARTS — one per row, full width, natural height */}
      <div className="flex flex-col gap-6">

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col items-center">
          <h3 className="w-full text-[13px] font-bold text-slate-800 tracking-wide mb-2">Regional Distribution</h3>
          <LeaderLineDonut data={apiData?.regionQuoteDistribution} centerLabel="Quotations"  onClick={(item) => handleRegionClick(item?.name)}/>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col items-center">
          <h3 className="w-full text-[13px] font-bold text-slate-800 tracking-wide mb-2">State-wise Distribution</h3>
          <LeaderLineDonut data={apiData?.stateQuoteDistribution} centerLabel="Quotations" onClick={(item) => handleStateClick(item?.name)} />
        </div>

      </div>

    </div>
  );
};

export default MonthlyTrendPreview;